import Phaser from 'phaser';
import type { GlyphName, HeroId, Special, TileColor } from '../core';
import { COSMETICS, GARLAND_FILE, LANTERN_FILE, cosmeticKey } from '../core/meta';
import { rasterFormat, rasterUrl } from './raster';
import type { HeroPose } from './cast';
import { HERO_ORDER, HERO_POSES, PAINTED_POSES } from './cast';
import { POWERUPS } from '../core/powerups';
import { glyphKey, trickIconKey } from './glyphs';
import { GRAIN_PLATES } from './woodGrain';
import { HUB_ICON, HUB_ICONS, queueHubIcons } from './hubIcons';

/**
 * Storybook Glossy keys. Slice margins match manifest.json:
 * Phaser order is left, right, top, bottom. Top and bottom of 0 make a 3-slice.
 */
export const TEX = {
  bgGame: 'bg-game',
  bgTitle: 'bg-title',
  boardFrame: 'board-frame',
  soil: 'board-soil',
  panelWood: 'panel-wood',
  panelParchment: 'panel-parchment',
  btnGold: 'btn-gold',
  btnGoldPressed: 'btn-gold-pressed',
  btnBerry: 'btn-berry',
  btnBerryPressed: 'btn-berry-pressed',
  meterFrame: 'meter-frame',
  meterFill: 'meter-fill',
  ribbon: 'ribbon',
  portraitFrame: 'portrait-frame',
  logo: 'logo',
  iconMoves: 'icon-moves',
  soundOn: 'icon-sound-on',
  soundOff: 'icon-sound-off',
  vfxPetal: 'vfx-petal',
  vfxLeaf: 'vfx-leaf',
  vfxPollen: 'vfx-pollen',
  vfxStar: 'vfx-star',
  vfxDust: 'vfx-dust',
  vfxDrop: 'vfx-drop',
  vfxFlame: 'vfx-flame',
  vfxRing: 'vfx-ring',
  shadow: 'fx-shadow',
  /** The bee cut from the chestnut bomb, perched on a fruit bomb. */
  bee: 'tile-bee',
  acornCurrency: HUB_ICON.acornCurrency,
  honeyCurrency: HUB_ICON.honeyCurrency,
  trophy: HUB_ICON.trophy,
  streak: HUB_ICON.streak,
  topbarPlank: 'topbar-plank',
  topbarStrip: 'topbar-strip',
  topbarFrame: 'topbar-frame',
  topbarBadge: 'topbar-badge',
  topbarTrophy: 'topbar-trophy',
  topbarAcorn: 'topbar-acorn',
  topbarHoney: 'topbar-honey',
  topbarPlus: 'topbar-plus',
  topbarGear: 'topbar-gear',
  topbarFlame: 'topbar-flame',
  hereMarker: 'marker-here',
  chestClosed: 'chest-closed',
  chestOpen: 'chest-open',
  prizeClosed: 'prize-box-closed',
  prizeOpen: 'prize-box-open',
  /** Carved Odds plaque from the prize wheel. */
  spinOdds: 'spin-odds-btn',
} as const;

export const SLICE = {
  board: { left: 120, right: 142, top: 125, bottom: 227 },
  boardInset: { left: 41, top: 33, right: 39, bottom: 29 },
  wood: { left: 72, right: 92, top: 50, bottom: 56 },
  parchment: { left: 24, right: 24, top: 24, bottom: 24 },
  button: { left: 44, right: 44 },
  meter: { left: 96, right: 130 },
  meterFill: { left: 44, right: 44 },
  ribbon: { left: 84, right: 84 },
} as const;

/** Meter frame is 380×152. The honey sits in this window before the honeycomb cap. */
export const METER = {
  texW: 380,
  texH: 152,
  fillX: 39,
  fillY: 35,
  fillRight: 118,
  fillH: 84,
  fillTexW: 297,
  fillTexH: 89,
} as const;

export const BUTTON = {
  gold: { w: 188, h: 86, normal: TEX.btnGold, pressed: TEX.btnGoldPressed },
  berry: { w: 180, h: 84, normal: TEX.btnBerry, pressed: TEX.btnBerryPressed },
} as const;

export const PORTRAIT_FRAME = 354;
export const PORTRAIT_HOLE = 240;
export const RIBBON_H = 104;

/**
 * The ribbon art is 104px tall, but the swallowtail forks hang below the
 * cream band. On `cc_ui_ribbon_banner@1x.png` the flat face is centered
 * this many pixels above the texture center. A 3-slice keeps that height
 * at every phone width, so the lift stays in design pixels.
 */
export const RIBBON_FACE_LIFT = 16;

/** Y for a label centered on the ribbon's flat face. `centerY` is the nine-slice center. */
export function ribbonFaceY(centerY: number, height = RIBBON_H): number {
  return Math.round(centerY - (RIBBON_FACE_LIFT * height) / RIBBON_H);
}

const GLYPH_FILE: Record<GlyphName, string> = {
  plus: 'cc_glyph_plus@2x.png',
  dots: 'cc_glyph_dots@2x.png',
  drop: 'cc_glyph_drop@2x.png',
  line: 'cc_glyph_line@2x.png',
  mail: 'cc_glyph_mail@2x.png',
  ring: 'cc_glyph_ring@2x.png',
  swap: 'cc_glyph_swap@2x.png',
  mask: 'cc_glyph_mask@2x.png',
  moon: 'cc_glyph_moon@2x.png',
  trail: 'cc_glyph_trail@2x.png',
  crack: 'cc_glyph_crack@2x.png',
  coin: 'cc_glyph_coin@2x.png',
  root: 'cc_glyph_root@2x.png',
  bomb: 'cc_glyph_bomb@2x.png',
  shield: 'cc_glyph_shield@2x.png',
  burst: 'cc_glyph_burst@2x.png',
  heart: 'cc_glyph_heart@2x.png',
  bolt: 'cc_glyph_bolt@2x.png',
  wave: 'cc_glyph_wave@2x.png',
  bubble: 'cc_glyph_bubble@2x.png',
  rain: 'cc_glyph_rain@2x.png',
  falls: 'cc_glyph_falls@2x.png',
  sprout: 'cc_glyph_sprout@2x.png',
  bloom: 'cc_glyph_bloom@2x.png',
};

const TILE_FILE: Record<TileColor, string> = {
  acorn: 'cc_tile_acorn@2x.png',
  berry: 'cc_tile_berry@2x.png',
  leaf: 'cc_tile_leaf@2x.png',
  blossom: 'cc_tile_blossom@2x.png',
  honey: 'cc_tile_honey@2x.png',
  moonstone: 'cc_tile_moonstone@2x.png',
};

const SPECIAL_FILE: Record<Special, string> = {
  'line-row': 'cc_tile_acorn_line_h@2x.png',
  'line-col': 'cc_tile_acorn_line_v@2x.png',
  bomb: 'cc_tile_chestnut_bomb@2x.png',
  rainbow: 'cc_tile_rainbow_orb@2x.png',
};

export function tileKey(color: TileColor): string {
  return `tile-${color}`;
}

export function specialKey(special: Special): string {
  return `tile-${special}`;
}

/** Painted honey bands baked onto that fruit. `h` is a row stripe, `v` a column stripe. */
export function stripeKey(color: TileColor, axis: 'h' | 'v'): string {
  return `tile-${color}-line-${axis}`;
}

export function signatureKey(id: HeroId): string {
  return `sig-${id}`;
}

/**
 * Body of the cell.
 * A stripe uses the painted line art for that fruit. A bomb keeps the fruit;
 * the scene adds a honey ring and a small corner bee. A rainbow is the colorless orb.
 */
export function tileTexture(color: TileColor, special: Special | null): string {
  if (special === 'rainbow') return specialKey('rainbow');
  if (special === 'line-row') return stripeKey(color, 'h');
  if (special === 'line-col') return stripeKey(color, 'v');
  return tileKey(color);
}

export function heroPoseKey(id: HeroId, pose: HeroPose): string {
  const art = pose === 'smug' ? 'boast' : pose;
  return `hero-${id}-${art}`;
}

export function portraitKey(id: HeroId): string {
  return `portrait-${id}`;
}

export function pawKey(id: HeroId): string {
  return `paw-${id}`;
}

/** Nothing shipped as a texture may exceed this. Phones drop larger uploads. */
export const TEXTURE_CAP = 2048;
/** Buttons, portraits, icons, and the chest stay at or under this. */
export const UI_TEXTURE_CAP = 1024;
/**
 * Charm cards and frames are painted at 1024 on the long side and shown near 224.
 * A phone that is already holding the duel art drops the extra full-size uploads.
 */
export const CHARM_TEXTURE_CAP = 512;

export interface ArtFile {
  key: string;
  path: string;
  /**
   * `raster` rewrites png/jpg to avif or webp.
   * `file` keeps the original path (no sibling).
   * `chest` prefers webp, then the png, so a failed compressed fetch can still paint.
   * `webp` always tries webp, then the png, ignoring the avif probe.
   */
  pack: 'raster' | 'file' | 'chest' | 'webp';
}

function art(key: string, path: string, pack: ArtFile['pack'] = 'raster'): ArtFile {
  return { key, path, pack };
}

/** Full-bleed paintings may use the stage height. Everything else is UI-sized. */
export function capForPath(path: string): number {
  if (path.includes('/bg/') || path.includes('cc_board_') || path.includes('/grain/')) return TEXTURE_CAP;
  if (path.includes('/charms/cards/') || path.includes('/charms/frames/')) return CHARM_TEXTURE_CAP;
  return UI_TEXTURE_CAP;
}

/**
 * Closed and open chests share one crop, with a soft alpha edge.
 * WebP keeps that edge. Avif still-picture subsamples alpha, so a browser
 * that would otherwise ask for avif gets webp first, then the png on retry.
 */
export function chestRequestUrl(path: string, attempt: number, format: 'avif' | 'webp' | 'original' = rasterFormat()): string {
  const webp = rasterUrl(path, 'webp');
  const png = path;
  const preferred = format === 'original' ? png : webp;
  const alternate = preferred === png ? webp : png;
  if (attempt <= 1) return preferred;
  if (attempt === 2) return alternate;
  const join = png.includes('?') ? '&' : '?';
  return `${png}${join}reload=${attempt}`;
}

/** Charm art skips avif. WebP first, then the original png. */
export function charmArtUrls(path: string): readonly [string, string] {
  return [rasterUrl(path, 'webp'), path];
}

/** The first fetch uses the plain URL. Later tries bypass a bad cache entry. */
/** The URL the first fetch should use for this painting. */
export function preferredArtUrl(path: string, pack: ArtFile['pack'] = 'raster'): string {
  return artUrl({ key: '', path, pack }, 1);
}

export function artUrl(file: ArtFile, attempt: number): string {
  if (file.pack === 'chest') return chestRequestUrl(file.path, attempt);
  if (file.pack === 'webp') {
    const [webp, png] = charmArtUrls(file.path);
    if (attempt <= 1) return webp;
    if (attempt === 2) return png;
    const join = png.includes('?') ? '&' : '?';
    return `${png}${join}reload=${attempt}`;
  }
  const path = file.pack === 'raster' ? rasterUrl(file.path) : file.path;
  if (attempt <= 1) return path;
  const join = path.includes('?') ? '&' : '?';
  return `${path}${join}reload=${attempt}`;
}

export function missingArt(textures: { exists(key: string): boolean }, files: readonly ArtFile[]): ArtFile[] {
  return files.filter((file) => !textures.exists(file.key));
}

/** Queue any of these files the texture manager does not already have. */
export function queueArt(scene: Phaser.Scene, files: readonly ArtFile[], attempt: number): void {
  for (const file of missingArt(scene.textures, files)) {
    scene.load.image(file.key, artUrl(file, attempt));
  }
}

/** Art the title paints, plus the chest so it is not left at the end of the duel batch. */
export function loadTitleArt(scene: Phaser.Scene): void {
  queueHubIcons(scene);
  queueArt(scene, titleArtFiles(), 1);
}

export function titleArtFiles(): ArtFile[] {
  const files: ArtFile[] = [
    art(TEX.bgTitle, 'assets/bg/cc_bg_title_thistlewood@1x.jpg'),
    art(TEX.panelParchment, 'assets/ui/cc_ui_panel_parchment@1x.png'),
    art(TEX.btnGold, 'assets/ui/cc_ui_btn_gold_normal@1x.png'),
    art(TEX.btnGoldPressed, 'assets/ui/cc_ui_btn_gold_pressed@1x.png'),
    art(TEX.btnBerry, 'assets/ui/cc_ui_btn_berry_normal@1x.png'),
    art(TEX.btnBerryPressed, 'assets/ui/cc_ui_btn_berry_pressed@1x.png'),
    art(TEX.portraitFrame, 'assets/ui/cc_ui_portrait_frame_round@1x.png'),
    art(TEX.logo, 'assets/ui/cc_ui_logo_wordmark_480w@1x.png'),
    art(TEX.soundOn, 'assets/icons/cc_icon_sound_on@1x.png'),
    art(TEX.soundOff, 'assets/icons/cc_icon_sound_off@1x.png'),
    art(TEX.vfxPetal, 'assets/vfx/cc_vfx_petal@1x.png'),
    art(TEX.vfxLeaf, 'assets/vfx/cc_vfx_leaf@1x.png'),
    art(TEX.vfxPollen, 'assets/vfx/cc_vfx_pollen_sparkle@1x.png'),
    art(TEX.chestClosed, 'assets/ui/cc_chest_closed@1x.png', 'chest'),
    art(TEX.chestOpen, 'assets/ui/cc_chest_open@1x.png', 'chest'),
    art(TEX.prizeClosed, 'assets/ui/cc_prize_box_closed@1x.png', 'chest'),
    art(TEX.prizeOpen, 'assets/ui/cc_prize_box_open@1x.png', 'chest'),
    art('tab-store', 'assets/ui/tabs/tab-store.png', 'webp'),
    art('tab-friends', 'assets/ui/tabs/tab-friends.png', 'webp'),
    art('tab-home', 'assets/ui/tabs/tab-home.png', 'webp'),
    art('tab-events', 'assets/ui/tabs/tab-events.png', 'webp'),
    art('tab-album', 'assets/ui/tabs/tab-album.png', 'webp'),
    ...GRAIN_PLATES.map((plate) => art(plate.key, plate.path, 'file')),
    art(TEX.topbarPlank, 'assets/ui/topbar/cc_topbar_plank.png', 'file'),
    art(TEX.topbarStrip, 'assets/ui/topbar/cc_topbar_streak_strip.png', 'file'),
    art(TEX.topbarFrame, 'assets/ui/topbar/cc_topbar_frame.png', 'file'),
    art(TEX.topbarBadge, 'assets/ui/topbar/cc_topbar_badge.png', 'file'),
    art(TEX.topbarTrophy, 'assets/ui/topbar/cc_topbar_icon_trophy.png', 'file'),
    art(TEX.topbarAcorn, 'assets/ui/topbar/cc_topbar_icon_acorn.png', 'file'),
    art(TEX.topbarHoney, 'assets/ui/topbar/cc_topbar_icon_honey.png', 'file'),
    art(TEX.topbarPlus, 'assets/ui/topbar/cc_topbar_icon_plus.png', 'file'),
    art(TEX.topbarGear, 'assets/ui/topbar/cc_topbar_icon_gear.png', 'file'),
    art(TEX.topbarFlame, 'assets/ui/topbar/cc_topbar_icon_streak.png', 'file'),
  ];
  for (const color of Object.keys(TILE_FILE) as TileColor[]) {
    files.push(art(tileKey(color), `assets/tiles/${TILE_FILE[color]}`));
  }
  for (const id of HERO_ORDER) {
    files.push(art(portraitKey(id), `assets/heroes/cc_hero_${id}_portrait_round@2x.png`));
  }
  return files;
}

/**
 * Everything the title does not draw.
 * A present board background does not skip the chest, cosmetics, or poses.
 */
export function loadDuelArt(scene: Phaser.Scene): void {
  queueArt(scene, duelArtFiles(), 1);
  scene.load.once('complete', () => copyUnpaintedPoses(scene));
}

export function duelArtFiles(): ArtFile[] {
  const files: ArtFile[] = [
    art(TEX.bgGame, 'assets/bg/cc_bg_gameplay_thistlewood@1x.jpg'),
    art(TEX.boardFrame, 'assets/board/cc_board_frame_9slice@1x.png'),
    art(TEX.soil, 'assets/board/cc_board_cell_soil@1x.png'),
    art(TEX.panelWood, 'assets/ui/cc_ui_panel_wood@1x.png'),
    art(TEX.meterFrame, 'assets/ui/cc_ui_meter_frame@1x.png'),
    art(TEX.meterFill, 'assets/ui/cc_ui_meter_fill_honey@1x.png'),
    art(TEX.ribbon, 'assets/ui/cc_ui_ribbon_banner@1x.png'),
    art(TEX.iconMoves, 'assets/icons/cc_icon_moves_acorn@1x.png'),
    art(TEX.vfxStar, 'assets/vfx/cc_vfx_star_glint@1x.png'),
    art(TEX.vfxDust, 'assets/vfx/cc_vfx_dust_puff@1x.png'),
    art(TEX.vfxDrop, 'assets/vfx/cc_vfx_water_drop@1x.png'),
    art(TEX.vfxFlame, 'assets/vfx/cc_vfx_foxfire_flame@1x.png'),
    art(TEX.vfxRing, 'assets/vfx/cc_vfx_light_ring@1x.png'),
    art(TEX.bee, 'assets/tiles/cc_tile_bee@2x.png'),
    art(TEX.chestClosed, 'assets/ui/cc_chest_closed@1x.png', 'chest'),
    art(TEX.chestOpen, 'assets/ui/cc_chest_open@1x.png', 'chest'),
    art(TEX.prizeClosed, 'assets/ui/cc_prize_box_closed@1x.png', 'chest'),
    art(TEX.prizeOpen, 'assets/ui/cc_prize_box_open@1x.png', 'chest'),
    art(TEX.spinOdds, 'assets/ui/spin/btn_odds.png', 'webp'),
  ];
  for (const color of Object.keys(TILE_FILE) as TileColor[]) {
    files.push(art(stripeKey(color, 'h'), `assets/tiles/cc_tile_${color}_line_h@2x.png`));
    files.push(art(stripeKey(color, 'v'), `assets/tiles/cc_tile_${color}_line_v@2x.png`));
  }
  for (const special of Object.keys(SPECIAL_FILE) as Special[]) {
    files.push(art(specialKey(special), `assets/tiles/${SPECIAL_FILE[special]}`));
  }
  for (const name of Object.keys(GLYPH_FILE) as GlyphName[]) {
    files.push(art(glyphKey(name), `assets/icons/${GLYPH_FILE[name]}`));
  }
  for (const id of HERO_ORDER) {
    files.push(art(signatureKey(id), `assets/icons/cc_icon_sig_${id}@2x.png`));
    if (PAINTED_POSES.has(id)) {
      for (const pose of HERO_POSES) {
        files.push(art(heroPoseKey(id, pose), `assets/heroes/cc_hero_${id}_${pose}_full@1x.png`));
      }
    } else {
      files.push(art(heroPoseKey(id, 'idle'), `assets/heroes/cc_hero_${id}_idle_full@1x.png`));
    }
    files.push(art(pawKey(id), `assets/heroes/cc_hero_${id}_paw@1x.png`));
  }
  const cosmeticFiles = new Set<string>();
  for (const item of COSMETICS) cosmeticFiles.add(item.file);
  cosmeticFiles.add(GARLAND_FILE);
  cosmeticFiles.add(LANTERN_FILE);
  for (const file of cosmeticFiles) files.push(art(cosmeticKey(file), `assets/cosmetics/${file}`, 'raster'));
  for (const power of POWERUPS) files.push(art(trickIconKey(power.id), `assets/tricks/cc_trick_${power.id}.png`, 'webp'));
  files.push(art(TEX.hereMarker, 'assets/icons/cc_marker_here.png', 'webp'));
  return files;
}

/**
 * Paintings on the Store cards: trick icons, cosmetic photos, the spin mark,
 * and the reward pictures. Charm cards are loaded with the charm batch.
 */
export function storeCardArtFiles(): ArtFile[] {
  const files: ArtFile[] = [];
  for (const power of POWERUPS) files.push(art(trickIconKey(power.id), `assets/tricks/cc_trick_${power.id}.png`, 'webp'));
  const cosmeticFiles = new Set<string>();
  for (const item of COSMETICS) cosmeticFiles.add(item.file);
  cosmeticFiles.add(GARLAND_FILE);
  cosmeticFiles.add(LANTERN_FILE);
  for (const file of cosmeticFiles) files.push(art(cosmeticKey(file), `assets/cosmetics/${file}`, 'raster'));
  files.push(art('spin-icon', 'assets/ui/spin/spin_icon.png', 'webp'));
  for (const name of ['acorns', 'honey', 'gift', 'tricks', 'crown', 'charm']) {
    files.push(art(`reward-${name}`, `assets/ui/rewards/reward-${name}.png`, 'webp'));
  }
  return files;
}

/** Later critters reuse the idle cutout for cheer, boast, dismay, power, and hurt. */
export function copyUnpaintedPoses(scene: Phaser.Scene): void {
  for (const id of HERO_ORDER) {
    if (PAINTED_POSES.has(id)) continue;
    const idleKey = heroPoseKey(id, 'idle');
    if (!scene.textures.exists(idleKey)) continue;
    const source = scene.textures.get(idleKey).getSourceImage() as HTMLImageElement & { width?: number };
    if (!source || (source.width ?? 0) < 8) continue;
    for (const pose of HERO_POSES) {
      if (pose === 'idle') continue;
      const key = heroPoseKey(id, pose);
      if (!scene.textures.exists(key)) scene.textures.addImage(key, source);
    }
  }
}

/** The generated chest box is 160×150. The painting is much larger, so a tiny chest is not art. */
export function isChestPlaceholder(width: number, height: number): boolean {
  return width > 0 && height > 0 && width <= 200 && height <= 200;
}

/** Drop a stand-in that was stamped onto the real chest key, so the file can replace it. */
export function releaseChestPlaceholder(scene: Phaser.Scene, key: string): void {
  if (key !== TEX.chestClosed && key !== TEX.chestOpen) return;
  if (!scene.textures.exists(key)) return;
  const source = scene.textures.get(key).getSourceImage() as { width?: number; height?: number };
  if (!isChestPlaceholder(source.width ?? 0, source.height ?? 0)) return;
  scene.textures.remove(key);
}

/** Every file-backed key, plus pose copies and the hub icons. */
export function referencedTextureKeys(): string[] {
  const keys = new Set<string>();
  for (const file of [...titleArtFiles(), ...duelArtFiles()]) keys.add(file.key);
  for (const icon of HUB_ICONS) keys.add(icon.key);
  for (const id of HERO_ORDER) {
    if (PAINTED_POSES.has(id)) continue;
    for (const pose of HERO_POSES) {
      if (pose === 'idle') continue;
      keys.add(heroPoseKey(id, pose));
    }
  }
  return [...keys];
}

/** Keys a scene can draw that have not been registered yet. */
export function missingReferencedTextures(textures: { exists(key: string): boolean }): string[] {
  return referencedTextureKeys().filter((key) => !textures.exists(key));
}

export function loadCritterArt(scene: Phaser.Scene): void {
  loadTitleArt(scene);
  loadDuelArt(scene);
}

/** A soft ellipse under each glyph. The pack has no separate shadow. */
export function makeShadowTexture(scene: Phaser.Scene): void {
  if (scene.textures.exists(TEX.shadow)) return;
  const g = scene.make.graphics({ x: 0, y: 0 }, false);
  g.fillStyle(0x000000, 0.5);
  g.fillEllipse(32, 16, 54, 20);
  g.generateTexture(TEX.shadow, 64, 32);
  g.destroy();
}
